import { downloadFile } from '#utils/download';
import { Button, Message } from '@bedrock/components';
import { SignOut } from '@bedrock/icons-react';
import React from 'react';
import {
  biddingExportAllProposals,
  demandCreateExportDemandPlanTask,
  demandExportCooperateData,
  demandExportExportAllProposals,
  ExportTaskResultDto,
  myWorkspaceCreateExportAllDemandTask,
  myWorkspaceCreateExportAllTrumpetTask,
  myWorkspaceCreateExportTodoDemandTask,
  myWorkspaceCreateExportTodoTrumpetTask,
  myWorkspaceGetExportDemandTaskResult,
  outsourceDemandSourceCreateExportDemandSourceKolTask,
  outsourceWorkbenchesGetExportDemandTaskResult,
  workbenchesExportDemand,
  v2TaskQuery, outsourceContentMarketQueryExportMonitorLinks, contentMarketQueryExportMonitorLinks,
  outsourceV2TaskQuery,
} from '#services/api';
import { ButtonSize } from '@bedrock/components/es/Button/Button';
import { ButtonProps } from '@bedrock/components/lib/Button';
import { buildCacheKey, clearCache, readCache, writeCache } from './cacheUtils';
import { $sType } from '@mpc/common/src/utils';

const EXPORT_FILE_KEY_MAP = {
  // 我的工作台常规采购全部列表
  DEMANDS_EXPORT_DEMAND: {
    taskApi: myWorkspaceCreateExportAllDemandTask,
    taskResultApi: myWorkspaceGetExportDemandTaskResult,
  },
  // 我的工作台常规采购待办列表
  DEMANDS_EXPORT_TODO_DEMAND: {
    taskApi: myWorkspaceCreateExportTodoDemandTask,
    taskResultApi: myWorkspaceGetExportDemandTaskResult,
  },
  // 常规采购列表
  DEMANDS_PLANNER_EXPORT: {
    taskApi: workbenchesExportDemand,
    taskResultApi: myWorkspaceGetExportDemandTaskResult,
  },
  // 我的工作台小喇叭待办列表
  TRUMPET_EXPORT_TODO_DEMAND: {
    taskApi: myWorkspaceCreateExportTodoTrumpetTask,
    taskResultApi: myWorkspaceGetExportDemandTaskResult,
  },
  // 我的工作台小喇叭全部列表
  TRUMPET_EXPORT_DEMAND: {
    taskApi: myWorkspaceCreateExportAllTrumpetTask,
    taskResultApi: myWorkspaceGetExportDemandTaskResult,
  },
  // 策划单报价信息
  PLAN_ORDER_QUOTE_INFO: {
    taskApi: demandCreateExportDemandPlanTask,
    taskResultApi: myWorkspaceGetExportDemandTaskResult,
  },
  // 批量提报达人
  BATCH_REPORTING_EXPERT: {
    taskApi: outsourceDemandSourceCreateExportDemandSourceKolTask,
    taskResultApi: outsourceWorkbenchesGetExportDemandTaskResult,
  },
  // 需求单-合作内容-导出
  DEMANDS_EXPORT_COOPERATE_DATA: {
    taskApi: demandExportCooperateData,
    taskResultApi: myWorkspaceGetExportDemandTaskResult,
  },
  // 常规采购询报价明细 - 导出全部方案
  DEMAND_EXPORT_ALL_PROPOSALS: {
    taskApi: demandExportExportAllProposals,
    taskResultApi: v2TaskQuery,
  },
  // 比选供应商比选方案及评分 - 导出全部方案
  BIDDING_EXPORT_ALL_PROPOSALS: {
    taskApi: biddingExportAllProposals,
    taskResultApi: v2TaskQuery,
  },

  // 检测链接excel导出
  MONITOR_LINKS_EXPORT: {
    taskApi: contentMarketQueryExportMonitorLinks,
    outsourceTaskApi: outsourceContentMarketQueryExportMonitorLinks,
    taskResultApi: v2TaskQuery,
    outsourceTaskResultApi: outsourceV2TaskQuery,
  }
};

interface IProps {
  exportKey: keyof typeof EXPORT_FILE_KEY_MAP;
  params: any;
  intervalTime?: number;
  buttonSize?: ButtonSize;
  buttonProps?: ButtonProps;
  buttonText?: string;
  enableCache?: boolean;
  cacheComponent?: string;
  getUserId?: () => string;
  cacheExpiry?: number;
}

interface PollingOptions {
  taskApi: () => Promise<any>;
  taskResultApi: (p: { task_id: string }) => Promise<any>;
  intervalTime: number;
  existingTaskId?: string;
  onTaskCreated?: (taskId: string) => void;
  onSuccess?: (data: ExportTaskResultDto) => void;
  onFailure?: () => void;
}

const exportFilePolling = async ({
  taskApi,
  taskResultApi,
  intervalTime,
  existingTaskId,
  onTaskCreated,
  onSuccess,
  onFailure,
}: PollingOptions) => {
  return new Promise<void>(async (resolve, reject) => {
    try {
      const taskId = existingTaskId ?? (await taskApi())?.data;
      onTaskCreated?.(taskId);
      const getExportResult = async () => {
        const resResult = await taskResultApi({ task_id: taskId })
          .catch(err => {
            onFailure?.();
            reject();
            return Promise.reject(err);
          });
        if (resResult?.data?.status === 1) {
          setTimeout(getExportResult, intervalTime);
        } else if (resResult?.data?.status === 2) {
          onFailure?.();
          Message.error('导出失败');
          reject();
        } else if (resResult?.data?.status === 3) {
          onSuccess?.(resResult.data);
          resResult?.data?.download_url && downloadFile(resResult.data.download_url);
          resolve();
        } else {
          onFailure?.();
          reject();
        }
      };
      setTimeout(getExportResult, intervalTime);
    } catch (error) {
      onFailure?.();
      reject();
    }
  });
};

const ExportFilePollingBtn: React.FC<IProps> = ({
  exportKey,
  params = {},
  intervalTime = 500,
  buttonSize = 'medium',
  buttonProps = {},
  buttonText,
  enableCache = false,
  cacheComponent,
  getUserId,
  cacheExpiry = 86400000,
}) => {
  const [loading, setLoading] = React.useState(false);
  const config = EXPORT_FILE_KEY_MAP[exportKey];
  const isOuter = $sType !== 'inner';
  const taskApi = (isOuter && config?.outsourceTaskApi) ? config.outsourceTaskApi : config?.taskApi;
  const taskResultApi = (isOuter && config?.outsourceTaskResultApi) ? config.outsourceTaskResultApi : config?.taskResultApi;

  const handleExport = async (e) => {
    e && e.stopPropagation();

    let existingTaskId: string | undefined;

    if (enableCache && cacheComponent) {
      const userId = getUserId?.() ?? 'anonymous';
      const cacheKey = buildCacheKey(cacheComponent, userId, params);
      const now = Date.now();
      const cached = readCache<ExportTaskResultDto>(cacheKey);

      if (cached && cached.expires_at > now) {
        if (cached.status === 'running') {
          existingTaskId = cached.task_id;
        } else {
          clearCache(cacheKey);
        }
      } else if (cached) {
        clearCache(cacheKey);
      }
    }

    setLoading(true);

    const cacheCallbacks = (enableCache && cacheComponent) ? (() => {
      const userId = getUserId?.() ?? 'anonymous';
      const key = buildCacheKey(cacheComponent, userId, params);
      const expiry = cacheExpiry ?? 86400000;
      return {
        onTaskCreated: (taskId: string) => {
          writeCache<ExportTaskResultDto>(key, {
            task_id: taskId,
            status: 'running',
            created_at: Date.now(),
            expires_at: Date.now() + expiry,
          });
        },
        onSuccess: () => clearCache(key),
        onFailure: () => clearCache(key),
      };
    })() : {};

    exportFilePolling({
      taskApi: () => taskApi(params),
      taskResultApi,
      intervalTime,
      existingTaskId,
      ...cacheCallbacks,
    }).finally(() => {
      setLoading(false);
    });
  };

  return (
    <Button size={buttonSize} icon={<SignOut />} type='checked-neutral' onClick={handleExport} loading={loading} {...buttonProps}>
      {buttonText || '导出'}
    </Button>
  );
};

export default ExportFilePollingBtn;
